import styled, { css } from 'styled-components'
import { Box } from '../../shared_components/UI/Box'
import { defaultTransition } from '../../theme'
import { getRgbaValue } from '../../utils/styling/getRgbaValue'

export const Container = styled(Box)`
  flex-direction: column;
  ${Box}, * {
    z-index: 10;
  }

  span.subsubtitle {
    color: #d3d7e4;
    font-size: 18px;
    font-style: normal;
    font-weight: 300;
    line-height: 24px; /* 133.333% */
    word-break: break-all;
  }

  .label {
    text-wrap: pretty;
  }
`

export const Subtitle = styled.span`
  color: #fff;
  font-size: 20px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  letter-spacing: 2px;
  text-transform: uppercase;
  padding: 0 0 16px 0;
`

export const TableRow = styled.div<{
  withBottomBorder?: boolean
  wide?: boolean
  zIndex?: number
}>`
  font-family: 'Space Grotesk';
  max-width: 660px;
  width: 100%;
  z-index: ${({ zIndex }) => zIndex ?? 4};
  display: grid;
  min-height: 56px;
  height: 100%;
  padding: 0 0 16px 0;
  align-items: center;
  grid-template-columns: 120px 1fr;
  ${({ withBottomBorder, theme }) =>
    withBottomBorder &&
    css`
      border-bottom: 1px solid
        ${getRgbaValue(theme.semantics.zap.surface.primary, 0.1)};
    `}
  ${({ wide }) =>
    wide &&
    css`
      display: flex;
    `}

  div.alert-container {
    align-items: center;
    margin-top: -20px;
    img {
      width: 18px;
      margin: 0 8px 0 0;
    }
  }
  span.label {
    font-family: 'Space Grotesk';
    font-size: 12px;
    line-height: 14px;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    opacity: 0.3;
  }
  span.submit-error {
    color: #ff623e;
    padding: 16px 0 8px;
    font-family: Space Grotesk;
    font-size: 16px;
    font-style: normal;
    font-weight: 300;
    line-height: normal;
  }
  span.tier {
    font-family: 'Space Grotesk';
    font-style: normal;
    font-weight: 300;
    font-size: 20px;
    line-height: 30px;
    text-transform: capitalize;
  }
  span.alert {
    font-family: 'Space Grotesk';
    text-align: right;
    font-size: 14px;
  }
  p.copy {
    opacity: 0.4;
    margin: 8px 0;
    z-index: 1;
  }
  a.shrunk {
    width: 174px;
  }
  a.shrunk.further {
    width: 122px;
  }
  button {
    margin: 0;
  }
  @media screen and (max-width: 600px) {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    span.label {
      margin-bottom: 16px;
    }
    ${Box} {
      flex-direction: column;
    }
    div.force-row {
      flex-direction: row;
    }
    div.mobile-top-margin {
      margin-top: 32px;
    }
    div.lith-plus {
      margin-top: -40px;
      width: 100%;
      margin-left: 0;
      align-items: flex-start;
      span.tier {
        font-size: 16px;
        margin-left: auto;
        margin-bottom: 8px;
      }
      button {
        min-width: calc(100vw - 64px) !important;
      }
    }
  }
`

export const CheckboxItem = styled(Box) <{ isSelected: boolean }>`
  margin: 8px 0;
  cursor: pointer;
  .custom-checkbox {
    opacity: ${({ isSelected }) => (isSelected ? 1 : 0.4)};
  }
  flex-direction: row !important;
  min-width: 50%;
  align-items: center;
  @media screen and (max-width: 600px) {
    margin: 12px 0;
    min-width: 100%;
  }
  p {
    color: #fff;
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
  }
`

export const EmailInput = styled.input<{ $width?: string }>`
  height: 44px;
  max-width: 100%;
  width: ${({ $width }) => $width ?? '240px'};
  background: ${({ theme: { isDark } }) =>
    `rgba(0, 0, 0, ${isDark ? 0.3 : 0.05})`};
  border-bottom: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 2px;
  outline: none;
  padding: 0 12px;
  font-family: 'Space Grotesk';
  :active,
  :focus {
    border-bottom: 1px solid rgba(255, 255, 255, 0.5);
  }
  ::placeholder {
    opacity: 0.6;
  }
  ${defaultTransition}
`

export const DropdownContainer = styled(Box)`
  position: relative;
  flex: 0;
  margin-right: 16px;
`

export const Button = styled.button`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100px;
  height: 56px;
  background: ${({ theme: { isDark } }) =>
    `rgba(0, 0, 0, ${isDark ? 0.3 : 0.05})`};
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 8px;
  outline: none;
  padding: 0 12px;
  img,
  span {
    opacity: 0.5;
  }
`

export const Options = styled.ul<{ isOpen: boolean }>`
  position: absolute;
  z-index: ${({ isOpen }) => (isOpen ? 10 : -5)};
  pointer-events: ${({ isOpen }) => (isOpen ? 'all' : 'none')};
  opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
  top: 64px;
  display: flex;
  flex-direction: column;
  width: 100px;
  max-height: 300px;
  overflow-y: scroll;
  border-radius: 8px;
  background: ${({ theme: { isDark } }) =>
    isDark ? 'rgba(0, 0, 0, 0.95)' : 'white'};
  border: 1px solid rgba(255, 255, 255, 0.3);

  li.search-box {
    z-index: 10;
    img.search-icon {
      filter: invert(${({ theme }) => (!theme.isDark ? 0 : 1)});
      opacity: 0.5;
      width: 24px;
    }
    :hover {
      background: rgba(255, 255, 255, 0.05);
      cursor: pointer;
    }
  }

  input {
    width: 100%;
    background: transparent;
    outline: none;
    margin-left: 8px;
  }

  ::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
  ${defaultTransition}
`

export const ListItem = styled.li<{ isSelected: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: 56px;
  text-align: center;
  padding: 0 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  transition: 0s;
  ${({ isSelected }) =>
    isSelected &&
    css`
      background: ${({ theme: { isDark } }) =>
        isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'};
    `}
  :hover {
    background: ${({ theme: { isDark } }) =>
    isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'};
    cursor: pointer;
  }
`

export const ClickListener = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: transparent;
  z-index: 1;
`

export const Divider = styled.div`
  height: 4px;
  width: 100%;
  opacity: 0.1;
  background: #fff;
  margin: 54px 0;
`

export const LinkedAccountRow = styled(Box)`
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  background: #16171b;
  padding: 16px 12px;

  .error {
    color: #ff623e;
    font-size: 16px;
    font-weight: 300;
    line-height: normal;
    text-align: right;
  }
  .warning {
    color: ${({ theme }) => theme.colors.brandPrimary};
    font-size: 10px;
    width: 60vw;
    max-width: 600px;
  }
`
